import { FC } from 'react';

import { NewFolderIcon, UploadArrowIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import {
  EmptyState,
  EmptyStateImageVariant,
  EmptyStateVariant,
} from '../../../../components/empty-state/index';
import { useLocale } from '../../../../hooks/index';

// Each action is absent where it is not allowed: an inactive room, the depth limit, a Vault folder
// that takes no files. The Vault never offers a new folder, only an upload where its rule allows.
type WorkspaceBrowserEmptyProps = {
  // An upload in flight keeps the button in place, inactive, as the toolbar's is.
  isUploading?: boolean;
  onCreateFolder?: () => void;
  onUpload?: () => void;
};

export const WorkspaceBrowserEmpty: FC<WorkspaceBrowserEmptyProps> = ({
  isUploading = false,
  onCreateFolder,
  onUpload,
}) => {
  const { messagesLocale } = useLocale();

  if (!onUpload && !onCreateFolder) {
    return (
      <EmptyState
        colorScheme={EmptyStateVariant.ORANGE}
        imageVariant={EmptyStateImageVariant.CLIPBOARD}
        text={messagesLocale['NO_FILES_OR_FOLDERS_YET']}
      />
    );
  }

  return (
    <EmptyState
      actions={
        <div className="flex items-center gap-6">
          {onUpload && (
            <Button
              className="h-9 w-fit gap-1 border border-brand-100 bg-brand-50 pl-4 pr-5 text-sm font-[550] text-brand-700"
              data-permission-required
              disabled={isUploading}
              onClick={onUpload}
              styleType="NONE"
            >
              <UploadArrowIcon iconColor="#2565C8" />
              <AppFormattedMessage id="UPLOAD_FILES" />
            </Button>
          )}
          {onCreateFolder && (
            <Button
              className="h-9 w-fit gap-1 py-0 pl-4 pr-5"
              data-permission-required
              onClick={onCreateFolder}
            >
              <NewFolderIcon iconColor="#ffffff" />
              <AppFormattedMessage id="CREATE_FOLDER" />
            </Button>
          )}
        </div>
      }
      colorScheme={EmptyStateVariant.ORANGE}
      // The hint names a folder, so where only an upload is offered the button speaks for itself.
      description={
        onCreateFolder ? (
          <span className="block text-label-md font-medium">
            {messagesLocale['UPLOAD_OR_CREATE_FOLDER_HINT']}
          </span>
        ) : undefined
      }
      imageVariant={EmptyStateImageVariant.CLIPBOARD}
      text={messagesLocale['NO_FILES_OR_FOLDERS_YET']}
    />
  );
};
